import React from "react";
import moment from "moment";
import {Box, Button, IconButton, Stack, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  Calendar03Icon, Cancel01Icon, Call02Icon, Clock01Icon, PencilEdit01Icon, Route01Icon, UserMultipleIcon
} from "@hugeicons/core-free-icons";
import {CampaignStatusChipsNew} from "../CampaignStatusChipsNew";
import {extensionLabel} from "../../../../project/campaigns/hooks/useAmiExtensions";
import {campaignColor, campaignState, STATE_TINTS} from "./calendarUtils";

const InfoRow = ({icon, label, children}) => (
  <Stack direction={"row"} alignItems={"center"} gap={1.25} sx={{minWidth: 0}}>
    <Box sx={{
      width: 32, height: 32, borderRadius: "10px", flexShrink: 0, display: "flex", alignItems: "center",
      justifyContent: "center", color: "#8491F7", bgcolor: "#FAFAFD",
    }}>
      <HugeiconsIcon icon={icon} size={16}/>
    </Box>
    <Box sx={{minWidth: 0}}>
      <Typography sx={{fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0", textTransform: "uppercase"}}>
        {label}
      </Typography>
      <Typography sx={{fontSize: 13, fontWeight: 500, color: "#23264D"}}>{children}</Typography>
    </Box>
  </Stack>
);

export const CampaignCalendarDetails = ({campaign, extension, language, onClose, onOpen, onEdit}) => {
  const state = STATE_TINTS[campaignState(campaign)];
  const color = campaignColor(campaign);
  const days = moment(campaign.endDate).diff(moment(campaign.startDate), "days") + 1;

  return (
    <Stack sx={{height: "100%", minHeight: 0}}>
      <Stack direction={"row"} alignItems={"flex-start"} justifyContent={"space-between"} gap={1}
             sx={{px: 2.5, pt: 2.5, pb: 1.5, background: `linear-gradient(135deg, ${color.bg} 0%, #fff 75%)`}}>
        <Box sx={{minWidth: 0}}>
          <Stack direction={"row"} alignItems={"center"} gap={0.75} flexWrap={"wrap"} sx={{mb: 0.75}}>
            <Box component={"span"} sx={{
              px: 1, height: 22, display: "inline-flex", alignItems: "center", borderRadius: "999px", fontSize: 11,
              fontWeight: 700, color: state.color, bgcolor: state.bg,
            }}>
              {state.label}
            </Box>
            {language && (
              <Box component={"span"} sx={{
                px: 1, height: 22, display: "inline-flex", alignItems: "center", borderRadius: "999px", fontSize: 11,
                fontWeight: 700, color: "#6B6B80", bgcolor: "#F2F2F7",
              }}>
                {language}
              </Box>
            )}
          </Stack>
          <Typography component={"h3"} sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em", wordBreak: "break-word"}}>
            {campaign.name}
          </Typography>
          {campaign.description && (
            <Typography sx={{mt: 0.5, fontSize: 13, color: "#8A8AA0"}}>{campaign.description}</Typography>
          )}
        </Box>
        <IconButton onClick={onClose} aria-label={"Close details"} sx={{
          width: 36, height: 36, flexShrink: 0, color: "#23264D", bgcolor: "#F2F2F7", "&:hover": {bgcolor: "#E6E6EF"},
        }}>
          <HugeiconsIcon icon={Cancel01Icon} size={18}/>
        </IconButton>
      </Stack>

      <Stack gap={1.75} sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2.5, py: 1.5}}>
        <InfoRow icon={Calendar03Icon} label={"Dates"}>
          {moment(campaign.startDate).format("DD MMM YYYY")} – {moment(campaign.endDate).format("DD MMM YYYY")}
          <Typography component={"span"} sx={{fontSize: 12, color: "#8A8AA0", ml: 0.75}}>
            ({days} {days === 1 ? "day" : "days"})
          </Typography>
        </InfoRow>
        <InfoRow icon={Clock01Icon} label={"Daily calling window"}>{campaign.startTime} – {campaign.endTime}</InfoRow>
        <InfoRow icon={Call02Icon} label={"From number"}>{campaign.fromNumber || "-"}</InfoRow>
        <InfoRow icon={Route01Icon} label={"Extension"}>
          {extensionLabel(extension) || (campaign.extensionId ? "Unknown" : "-")}
        </InfoRow>
        <InfoRow icon={UserMultipleIcon} label={"Phone numbers"}>
          {campaign.totalPhoneNumbers ?? 0}
          <Typography component={"span"} sx={{fontSize: 12, color: "#8A8AA0", ml: 0.75}}>
            · max {campaign.maxAttempts} {campaign.maxAttempts === 1 ? "attempt" : "attempts"} each
          </Typography>
        </InfoRow>
        <Box>
          <Typography sx={{mb: 0.75, fontSize: 10, fontWeight: 600, letterSpacing: "0.08em", color: "#8A8AA0", textTransform: "uppercase"}}>
            Call outcomes
          </Typography>
          <CampaignStatusChipsNew statusCounts={campaign.statusCounts}/>
        </Box>
      </Stack>

      <Stack direction={"row"} gap={1} sx={{px: 2.5, pt: 1.5, pb: 2.5}}>
        <Button
          fullWidth
          onClick={() => onOpen(campaign)}
          sx={{
            height: 42, borderRadius: "12px", textTransform: "none", fontSize: 13, fontWeight: 700, color: "#374151",
            bgcolor: "#F2F2F7", "&:hover": {bgcolor: "#E6E6EF"},
          }}
        >
          Open campaign
        </Button>
        <IconButton onClick={() => onEdit(campaign)} aria-label={"Edit campaign"} sx={{
          width: 42, height: 42, borderRadius: "12px", color: "#374151", bgcolor: "#F2F2F7", "&:hover": {bgcolor: "#E6E6EF"},
        }}>
          <HugeiconsIcon icon={PencilEdit01Icon} size={18}/>
        </IconButton>
      </Stack>
    </Stack>
  );
};
